//@flow
import React from 'react';
import { connect } from 'react-redux';
import AvatarHoverCard from 'app/common/react/components/AvatarHoverCard/AvatarHoverCard';
import CreditAvatar from 'app/common/react/components/DeprecatedPinRep/attribution/CreditAvatar';
import CreditName from 'app/common/react/components/DeprecatedPinRep/attribution/CreditName';
import CreditTitle from 'app/common/react/components/DeprecatedPinRep/attribution/CreditTitle';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import type { AttributedPin } from 'app/common/react/components/DeprecatedPinRep/attribution/types';
import type { Dispatch } from 'redux';
import { Box } from 'gestalt';
import { setMostRecentPin } from 'app/common/redux/actions/mostRecentPin';
import { track } from 'app/common/lib/react/analytics';

type ReduxDispatchProps = {|
  setMostRecentPin: (pin_id: string) => void,
|};

type OwnProps = {|
  hideTitle?: boolean,
  pin: AttributedPin,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
|};

type Props = {|
  ...ReduxDispatchProps,
  ...OwnProps,
|};

const AVATAR_SIZE_PIXELS = 32;

const isPromotedPin = (pin: AttributedPin) => !!pin.promoter && !pin.is_downstream_promotion;

export function PinCredits({ hideTitle, pin, viewType, viewParameter, setMostRecentPin }: Props) {
  const { board, promoter } = pin;
  const pinner = pin.pinner || {};
  const isPromoted = isPromotedPin(pin);
  const user = pin.promoter || pinner;

  const handlePinCreditClick = () => {
    setMostRecentPin(pin.id);
    track(PCONST.thrift.EventType.CLICK, {
      view: viewType,
      viewParameter,
      element: isPromoted
        ? PCONST.thrift.ElementType.PIN_CELL_BUTTON_PROMOTED_PIN_SOURCE
        : PCONST.thrift.ElementType.PIN_ATTRIBUTION,
      objectId: pin.id,
    });
  };

  let url, name, creditNameLink, creditTitle, creditTitleLink, avatarUrl;

  if (isPromoted && promoter) {
    url = `/${promoter.username}/`;
    name = i18n._('Promoted by');
    creditTitle = promoter.full_name;
    avatarUrl = promoter.image_small_url;
  } else {
    url = board && board.url;
    name = pinner.full_name;
    creditTitle = board && board.name && board.privacy !== 'protected' ? board.name : '';
    avatarUrl = pinner.image_small_url;
    creditNameLink = `/${pinner.username}/`;
    creditTitleLink = board ? board.url : '';
  }

  // Determines whether a promoted pin is from a user with an account. Used to determine whether or not to show AvatarHoverCard.
  const isDarkPromoter = !isPromoted && !!promoter;

  const body = (
    <Box display="flex" direction="row" alignItems="center" marginLeft={-1} marginRight={-1}>
      <Box paddingX={1}>
        <AvatarHoverCard
          user={user}
          username={user && user.username}
          avatarSize={AVATAR_SIZE_PIXELS}
          isDarkPromoter={isDarkPromoter}
          pinId={pin.id}
          viewParameter={viewParameter}
        >
          <CreditAvatar avatarUrl={avatarUrl} creditNameLink={creditNameLink} name={name} />
        </AvatarHoverCard>
      </Box>
      <Box paddingX={1} overflow="hidden">
        <CreditName creditNameLink={creditNameLink} name={name} />
        {!hideTitle && <CreditTitle link={creditTitleLink} title={creditTitle} />}
      </Box>
    </Box>
  );

  if (url && !creditNameLink && !creditTitleLink) {
    return (
      <NavigateLink
        className="underlineLink"
        stopPropagation
        onClick={handlePinCreditClick}
        to={url}
      >
        {body}
      </NavigateLink>
    );
  }

  return body;
}

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  setMostRecentPin: (pin_id) => dispatch(setMostRecentPin(pin_id)),
});

export default connect<Props, OwnProps, _, _, _, _>(
  null,
  mapDispatchToProps,
)(PinCredits);
